import { z } from 'zod';
import { DateTime } from 'luxon';
import { apiEnvelope } from '../envelope';

/**
 * Weekly-plan topic suggestions — the domain contract between the model, the
 * server, and the client.
 *
 * The split that matters here: `weeklyPlanModelResponseSchema` is what the model
 * is allowed to say, and it carries NO identifiers. Ids are minted server-side
 * and grafted on afterwards, so a model that emits an `id` has it discarded
 * rather than honoured — the schema is `.strict()`, so it is rejected outright.
 * Nothing downstream can mistake a model-authored string for an opaque handle.
 */

/** How many topics a set always holds. Not a range — the product shows three. */
export const WEEKLY_PLAN_SUGGESTION_COUNT = 3;

/**
 * Ranks a valid set covers, exactly once each. Held as a sorted tuple rather
 * than a max, because "three suggestions" and "ranks 1, 2, 3" are different
 * claims: a payload can satisfy the count and still rank them `1, 1, 2`.
 */
export const WEEKLY_PLAN_RANKS = [1, 2, 3] as const;

/**
 * Title cap. The title is the option card's bold heading — one line at the
 * card's width — so this is a layout bound, not a stylistic preference.
 */
export const WEEKLY_PLAN_TITLE_MAX_CHARS = 40;

/**
 * Category cap. The category renders inside a compact pill above the title, so
 * past this the pill wraps onto a second line and stops reading as a tag. Equal
 * to the title cap by coincidence of layout, not by rule — the two are separate
 * constants so either can move without dragging the other.
 */
export const WEEKLY_PLAN_CATEGORY_MAX_CHARS = 40;

/**
 * Blurb bounds. The prompt asks for roughly 50–80 characters; these are the
 * enforced walls, deliberately wider than the target. A blurb at 45 or 95 is a
 * quality wobble that should still reach the member; one at 5 or 500 is a
 * contract breach. Rejecting the near-misses would discard usable copy over a
 * style guideline.
 */
export const WEEKLY_PLAN_BLURB_MIN_CHARS = 40;
export const WEEKLY_PLAN_BLURB_MAX_CHARS = 100;
/** The band the prompt asks for, recorded so drift between the two is visible. */
export const WEEKLY_PLAN_BLURB_TARGET_CHARS = { min: 50, max: 80 } as const;

/**
 * Kind-label cap. The kind is the model's own short name for the angle a theme
 * takes ("a friendship you want to rekindle"). It is internal — stored for later
 * evaluation of variety, never rendered, never sent to the client, never placed
 * in telemetry — so the cap is a storage bound, not a layout one. Deliberately
 * NOT an enum: the product decision is that themes may be about anything, and a
 * fixed vocabulary would push every theme back toward the same few boxes.
 */
export const WEEKLY_PLAN_KIND_MAX_CHARS = 40;

/** How long a generated set stays selectable. Read-time only — nothing sweeps. */
export const WEEKLY_PLAN_SUGGESTION_TTL_HOURS = 24;

/** Fewest and most topics a member may carry into their week. */
export const WEEKLY_PLAN_MIN_SELECTION = 1;
export const WEEKLY_PLAN_MAX_SELECTION = 3;

const rankSchema = z.union([z.literal(1), z.literal(2), z.literal(3)]);

const titleSchema = z.string().trim().min(1).max(WEEKLY_PLAN_TITLE_MAX_CHARS);
const categorySchema = z.string().trim().min(1).max(WEEKLY_PLAN_CATEGORY_MAX_CHARS);
const blurbSchema = z
  .string()
  .trim()
  .min(WEEKLY_PLAN_BLURB_MIN_CHARS)
  .max(WEEKLY_PLAN_BLURB_MAX_CHARS);
const kindSchema = z.string().trim().min(1).max(WEEKLY_PLAN_KIND_MAX_CHARS);

/**
 * Issue message the no-digits rule raises, so the adapter can tell "wrote a
 * number" apart from "wrote too much" without inspecting the value.
 */
export const WEEKLY_PLAN_DIGITS_ISSUE = 'digits';

/**
 * Member-facing theme copy — the category pill, the title and the blurb —
 * carries no digit. A theme is a qualitative area of
 * attention, and every quantitative framing the product wants to avoid — a
 * count, a target, a limit, a reduction — arrives as a numeral. Rejecting the
 * character rather than the phrasing is the one form of that rule the server
 * can enforce mechanically; the prompt carries the rest.
 */
const noDigits = <T extends z.ZodType<string>>(schema: T) =>
  schema.refine((value) => !/\d/.test(value), { message: WEEKLY_PLAN_DIGITS_ISSUE });

/**
 * One topic as the MODEL is allowed to return it: content and a rank, no id.
 * `.strict()` so a model that volunteers an `id`, a `suggestionSetId`, or a
 * nested render spec fails the parse instead of having the extra field ignored
 * — an ignored field is an injection surface that looks like it worked.
 */
export const weeklyPlanModelSuggestionSchema = z
  .object({
    rank: rankSchema,
    category: noDigits(categorySchema),
    title: noDigits(titleSchema),
    blurb: noDigits(blurbSchema),
    /** The model's own label for the theme's angle; free text, bounded, internal. */
    kind: kindSchema,
    /**
     * Request-local opaque reference; the server maps it to an active enrollment.
     * An omitted key means the same as `null` (cross-program) — a model that
     * drops the field for a member with one program must not fail the parse.
     */
    programRef: z.string().min(1).nullish(),
  })
  .strict();

export type WeeklyPlanModelSuggestion = z.infer<typeof weeklyPlanModelSuggestionSchema>;

/** Every rank present exactly once — the check the array length cannot make. */
function ranksAreTheCompleteSet(suggestions: readonly { rank: number }[]): boolean {
  const seen = new Set(suggestions.map((s) => s.rank));
  return seen.size === WEEKLY_PLAN_RANKS.length && WEEKLY_PLAN_RANKS.every((r) => seen.has(r));
}

export const weeklyPlanModelResponseSchema = z
  .object({
    suggestions: z.array(weeklyPlanModelSuggestionSchema).length(WEEKLY_PLAN_SUGGESTION_COUNT),
  })
  .strict()
  .superRefine((response, ctx) => {
    if (!ranksAreTheCompleteSet(response.suggestions)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['suggestions'],
        message: 'ranks must be exactly 1, 2 and 3 with no duplicates',
      });
    }
  });

export type WeeklyPlanModelResponse = z.infer<typeof weeklyPlanModelResponseSchema>;

/** A topic once the server has given it an opaque handle. */
export const weeklyPlanSuggestionSchema = z
  .object({
    id: z.string().min(1),
    rank: rankSchema,
    category: categorySchema,
    title: titleSchema,
    blurb: blurbSchema,
    /**
     * Optional because sets stored before the label existed must still parse;
     * the save path reads rows it did not write.
     */
    kind: kindSchema.optional(),
    /**
     * Validated association: `null` for a cross-program theme, otherwise one of
     * the member's active enrollments. Required-nullable like the completed
     * theme contract, so a stored suggestion can never be missing the key and
     * silently read as cross-program.
     */
    memberProgramId: z.string().uuid().nullable(),
  })
  .strict();

export type WeeklyPlanSuggestion = z.infer<typeof weeklyPlanSuggestionSchema>;

/**
 * The stored suggestion list. Typed as the `jsonb` column's contents (ADR-066),
 * so the row type and the wire type cannot drift apart.
 */
export const weeklyPlanSuggestionsSchema = z
  .array(weeklyPlanSuggestionSchema)
  .length(WEEKLY_PLAN_SUGGESTION_COUNT);

export type WeeklyPlanSuggestions = z.infer<typeof weeklyPlanSuggestionsSchema>;

/**
 * The member's kept ids, as stored. Canonical rank order is a storage
 * invariant, not a formatting nicety — see `canonicalSelectionOrder`.
 */
export const weeklyPlanSelectionSchema = z
  .array(z.string().min(1))
  .min(WEEKLY_PLAN_MIN_SELECTION)
  .max(WEEKLY_PLAN_MAX_SELECTION)
  .refine((ids) => new Set(ids).size === ids.length, {
    message: 'selected ids must be unique',
  });

/**
 * Save request. `.strict()` is the load-bearing part: it is what turns a
 * client-supplied `category`, `title` or `blurb` into a rejected request rather
 * than a silently-ignored field, so the stored copy can only ever be the
 * server's.
 */
export const saveWeeklyPlanTopicsRequestSchema = z
  .object({
    suggestionSetId: z.string().min(1),
    selectedSuggestionIds: weeklyPlanSelectionSchema,
  })
  .strict();

export type SaveWeeklyPlanTopicsRequest = z.infer<typeof saveWeeklyPlanTopicsRequestSchema>;

/**
 * What a successful save returns: the set's identity plus the kept topics with
 * the server's own copy. The client never supplies category, title or blurb, so
 * echoing them back is how it learns what was actually stored.
 */
export const saveWeeklyPlanTopicsResultSchema = z
  .object({
    suggestionSetId: z.string().min(1),
    selected: z.array(weeklyPlanSuggestionSchema).min(WEEKLY_PLAN_MIN_SELECTION),
    selectedAt: z.string().min(1),
  })
  .strict();

export type SaveWeeklyPlanTopicsResult = z.infer<typeof saveWeeklyPlanTopicsResultSchema>;

export const saveWeeklyPlanTopicsResponseSchema = apiEnvelope(saveWeeklyPlanTopicsResultSchema);
export type SaveWeeklyPlanTopicsResponse = ReturnType<
  typeof saveWeeklyPlanTopicsResponseSchema.parse
>;

/**
 * Sort kept ids by their rank in the set.
 *
 * The single writer of stored order, so submission order can never reach the
 * database. That is what makes idempotency decidable: without it `[b, a]` and
 * `[a, b]` are two different stored values for one member decision, and a
 * repeat request could not be recognised as a repeat.
 *
 * Ids absent from the set are dropped rather than appended — the caller has
 * already rejected them by then, and appending would let an unknown id survive
 * a validation gap.
 */
export function canonicalSelectionOrder(
  selectedIds: readonly string[],
  suggestions: readonly WeeklyPlanSuggestion[],
): string[] {
  const rankById = new Map(suggestions.map((s) => [s.id, s.rank] as const));
  return selectedIds
    .flatMap((id) => {
      const rank = rankById.get(id);
      return rank === undefined ? [] : [{ id, rank }];
    })
    .sort((a, b) => a.rank - b.rank)
    .map(({ id }) => id);
}

/**
 * Whether a set generated at `generatedAt` is still selectable at `now`.
 *
 * Inclusive at the boundary: a set exactly at its TTL is live. The alternative
 * makes expiry depend on millisecond timing at the edge, which is untestable
 * and unexplainable to a member who tapped one second late.
 */
export function isSuggestionSetLive(
  generatedAt: Date,
  now: Date,
  ttlHours: number = WEEKLY_PLAN_SUGGESTION_TTL_HOURS,
): boolean {
  // Luxon rather than `getTime()` arithmetic: ADR-096 Rule 5 puts date
  // arithmetic and comparison through Luxon in new code, and native `Date` is
  // transport-only. Expressed as "the expiry instant has not yet passed" so the
  // inclusive boundary below is a property of the comparison rather than of a
  // millisecond subtraction.
  const expiresAt = DateTime.fromJSDate(generatedAt).plus({ hours: ttlHours });
  return expiresAt >= DateTime.fromJSDate(now);
}
